import { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

// Compact popover portaled to document.body and anchored to a host element
// (`anchorRef`). Repositions on scroll/resize, closes on outside-click + Esc.
// `mousedown.preventDefault` on the wrapper pins focus to the anchor so any
// click inside doesn't blur the input that owns the popover.
export function AnchoredPopover({
  open,
  onClose,
  anchorRef,
  width,
  className = '',
  children,
}) {
  const [rect, setRect] = useState(null)
  const wrapRef = useRef(null)

  useEffect(() => {
    if (!open) return
    function update() {
      if (!anchorRef?.current) return
      setRect(anchorRef.current.getBoundingClientRect())
    }
    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [open, anchorRef])

  useEffect(() => {
    if (!open) return
    function onDown(e) {
      if (anchorRef?.current && anchorRef.current.contains(e.target)) return
      if (wrapRef.current   && wrapRef.current.contains(e.target))   return
      onClose()
    }
    function onKey(e) { if (e.key === 'Escape') onClose() }
    document.addEventListener('mousedown', onDown)
    document.addEventListener('touchstart', onDown, { passive: true })
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('touchstart', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, onClose, anchorRef])

  if (!open || !rect) return null

  // Clamp horizontally inside viewport
  const ww = window.innerWidth
  const w  = width ?? 320
  let left = rect.left
  if (left + w > ww - 8) left = ww - w - 8
  if (left < 8) left = 8

  return createPortal(
    <div
      ref={wrapRef}
      className={className}
      style={{ position: 'fixed', top: rect.bottom + 4, left, width: w }}
      onMouseDown={e => e.preventDefault()}
    >
      {children}
    </div>,
    document.body
  )
}
